import { Button } from "@/components/common/Button";
import { useUserStore } from "@/modules/auth/store/user";
import { useTonProofPayload } from "@/modules/wallet/hooks/useTonProofPayload";
import { useTonWalletProof } from "@/modules/wallet/hooks/useTonWalletProof";
import { AnalyticsEvents } from "@/shared/utils/analytics/events";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";
import RefcodeVerify from "../modal/RefcodeVerify";
import RefcodeReopen from "../modal/RefcodeReopen";
import { useCallback, useState } from "react";
import WebApp from "@twa-dev/sdk";
import SelectWallet from "../modal/SelectWallet";
import { postReferralApply } from "@/modules/auth/api/referral/apply";
import { useApplyRefcodeStore } from "@/modules/auth/store/applyRefcode";

export const TonConnectButton = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { user, activated, invitedBy, setReferral } = useUserStore();
  const { setIsReferralError } = useApplyRefcodeStore();

  const isMiniApp = !!WebApp.initData;

  useTonWalletProof();
  useTonProofPayload();

  const [tonConnectUI] = useTonConnectUI();

  const [searchParams] = useSearchParams();

  const [isVerifyOpen, setVerifyOpen] = useState(false);
  const [isReopenOpen, setReopenOpen] = useState(false);
  const [isWalletSelectionOpen, setWalletSelectionOpen] = useState(false);

  const handleClickTON = useCallback(() => {
    setWalletSelectionOpen(false);
    tonConnectUI.disconnect();
    tonConnectUI.openModal();
  }, [tonConnectUI]);

  const handleReferralApply = useCallback(async () => {
    const refcodeParam = searchParams.get("refcode") || "";
    const payload: {
      source: "miniapp" | "web";
      referralCode?: string;
      confirm: boolean;
    } = {
      source: isMiniApp ? "miniapp" : "web",
      referralCode: refcodeParam,
      confirm: user?.walletId ? true : false,
    };

    const referral = await postReferralApply(payload);
    if (referral) {
      setReferral(referral);
      if (referral.userReferralCode) {
        setIsReferralError(false);
      }
    }
  }, [isMiniApp, searchParams, setIsReferralError, setReferral, user?.walletId]);

  const handleClickEVM = useCallback(async () => {
    setWalletSelectionOpen(false);

    await handleReferralApply();
  }, [handleReferralApply]);

  const handlePrimaryButtonClick = useCallback(() => {
    setVerifyOpen(true);
  }, []);

  const handleVerifyYes = useCallback(async () => {
    setVerifyOpen(false);

    await handleReferralApply();

    if (isMiniApp) {
      handleClickTON();
      return;
    }

    if (user?.walletId) {
      return;
    }

    setWalletSelectionOpen(true);
  }, [handleClickTON, handleReferralApply, isMiniApp, user?.walletId]);

  const handleVerifyNo = useCallback(() => {
    setVerifyOpen(false);
    setReopenOpen(true);
  }, []);

  const handleReopenClose = useCallback(() => {
    setReopenOpen(false);
  }, []);

  const handleWalletSelectionClose = useCallback(() => {
    setWalletSelectionOpen(false);
  }, []);

  const analyticsEvent = location.pathname.startsWith("/settings")
    ? AnalyticsEvents.Settings.ConnectWallet
    : AnalyticsEvents.MainPage.ConnectWallet;

  if (activated === false && invitedBy)
    return (
      <>
        <Button
          onClick={handlePrimaryButtonClick}
          text={
            user?.walletId
              ? "Confirm sponsor"
              : isMiniApp
              ? t("main-page.connect_wallet")
              : t("main-page.connect_any_wallet")
          }
          size="small"
          pilled
          disabled={!user}
          analyticsEvent={analyticsEvent}
        />

        {isVerifyOpen && (
          <RefcodeVerify
            onYes={handleVerifyYes}
            onNo={handleVerifyNo}
            onClose={handleVerifyNo}
          />
        )}

        {isWalletSelectionOpen && (
          <SelectWallet
            onClose={handleWalletSelectionClose}
            onTON={handleClickTON}
            onEVM={handleClickEVM}
          />
        )}

        {isReopenOpen && <RefcodeReopen onClose={handleReopenClose} />}
      </>
    );

  return <></>;
};
